import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallback from './pages/AuthCallback';
import SearchPage from './pages/SearchPage';
import ProfessionalProfile from './pages/ProfessionalProfile';
import UserDashboard from './pages/UserDashboard';
import ProfessionalDashboard from './pages/ProfessionalDashboard';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import PremiumPage from './pages/PremiumPage';
import AdminDashboard from './pages/AdminDashboard';
import RequestPage from './pages/RequestPage';
import RequestRouteSwitch from './components/RequestRouteSwitch';
import ProfessionalProfileEdit from './pages/ProfessionalProfileEdit';
import CrmDashboard from './pages/CrmDashboard';
import TaxonomyPage from './pages/TaxonomyPage';
import WalletPage from './pages/WalletPage';
import RegisterProfessionalWizard from './pages/RegisterProfessionalWizard';
import PublicServicesPage from './pages/PublicServicesPage';
import MyRequestsPage from './pages/MyRequestsPage';
import PatientRecordPage from './pages/PatientRecordPage';
import HowItWorksPage from './pages/HowItWorksPage';
import ProPrestazioniPage from './pages/ProPrestazioniPage';
import ProRichiestePage from './pages/ProRichiestePage';
import ProAgendaPage from './pages/ProAgendaPage';
import CenterStaffPage from './pages/CenterStaffPage';
import InternalProPage from './pages/InternalProPage';
import CoveragePage from './pages/CoveragePage';
import ClientPromotionsPage from './pages/ClientPromotionsPage';
import ProAnalyticsPage from './pages/ProAnalyticsPage';
import RegisterCentroPage from './pages/RegisterCentroPage';
import MessagesPage from './pages/MessagesPage';
import AdminMessagesPage from './pages/AdminMessagesPage';
import AdminUncoveredZonesPage from './pages/AdminUncoveredZonesPage';
import AdminServiceProposalsPage from './pages/AdminServiceProposalsPage';
import SettingsPage from './pages/SettingsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminDocsPage from './pages/AdminDocsPage';
import GuidePage from './pages/GuidePage';
import AccountPage from './pages/AccountPage';
import ProPromotionsPage from './pages/ProPromotionsPage';
import AcceptAdminInvitePage from './pages/AcceptAdminInvitePage';
import AcceptCenterInvitePage from './pages/AcceptCenterInvitePage';
import IdleTimeoutGuard from './components/IdleTimeoutGuard';
import PendingRequestSubmitter from './components/PendingRequestSubmitter';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import CenterQuotesPage from './pages/CenterQuotesPage';
import ReviewsPage from './pages/ReviewsPage';
import ClientQuotesPage from './pages/ClientQuotesPage';
import './App.css';

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/come-funziona" element={<HowItWorksPage />} />
      <Route path="/prestazioni" element={<PublicServicesPage />} />
      <Route path="/register-professional" element={<RegisterProfessionalWizard />} />
      <Route
        path="/professional/completa-profilo"
        element={
          <ProtectedRoute>
            <DashboardLayout><RegisterProfessionalWizard embedded /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/register-centro" element={<RegisterCentroPage />} />
      <Route path="/professional/:id" element={<ProfessionalProfile />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout><UserDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional-dashboard"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><ProfessionalDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/premium" element={<PremiumPage />} />
      <Route path="/request" element={<RequestRouteSwitch />} />
      <Route
        path="/dashboard/nuova-richiesta"
        element={
          <ProtectedRoute>
            <DashboardLayout><RequestPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional-profile/edit"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><ProfessionalProfileEdit /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/wallet"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><WalletPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/prestazioni"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><ProPrestazioniPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/richieste"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><ProRichiestePage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/agenda"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><ProAgendaPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/organico"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><CenterStaffPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/copertura"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><CoveragePage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/internal"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><InternalProPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/analytics"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><ProAnalyticsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/richieste"
        element={
          <ProtectedRoute>
            <DashboardLayout><MyRequestsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/cartella"
        element={
          <ProtectedRoute>
            <DashboardLayout><PatientRecordPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/promozioni"
        element={
          <ProtectedRoute>
            <DashboardLayout><ClientPromotionsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/preventivi"
        element={
          <ProtectedRoute>
            <DashboardLayout><ClientQuotesPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/recensioni"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><ReviewsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/preventivi"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><CenterQuotesPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/messaggi"
        element={
          <ProtectedRoute>
            <DashboardLayout><MessagesPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professional/messaggi"
        element={
          <ProtectedRoute requireProfessional>
            <DashboardLayout><MessagesPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/messaggi"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><AdminMessagesPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/impostazioni"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><SettingsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin-invite/:token" element={<AcceptAdminInvitePage />} />
      <Route path="/center-invite/:token" element={<AcceptCenterInvitePage />} />
      <Route
        path="/professional/promozioni"
        element={
          <ProtectedRoute>
            <DashboardLayout><ProPromotionsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <DashboardLayout><AccountPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/guida"
        element={
          <ProtectedRoute>
            <DashboardLayout><GuidePage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/documentazione"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><AdminDocsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><AnalyticsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/professionisti"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><AdminDashboard section="professionals" /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/centri"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><AdminDashboard section="centers" /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/prestazioni-proposte"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><AdminServiceProposalsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/zone-scoperte"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><AdminUncoveredZonesPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bisogni-zone"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><AdminDashboard section="taxonomy" /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/strumenti"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><AdminDashboard section="tools" /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/crm"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><CrmDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pipeline"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><CrmDashboard section="pipeline" /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pagamenti"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><CrmDashboard section="payments" /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clienti"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><CrmDashboard section="customers" /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import-export"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><CrmDashboard section="export" /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/taxonomy"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout><TaxonomyPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <DashboardLayout><AdminDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <AppRouter />
          <IdleTimeoutGuard />
          <PendingRequestSubmitter />
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
